import React, { useMemo } from 'react';
import { Card, Spinner } from 'reactstrap'; // Card is kept for error/empty messages
import {
  Block,
  BlockBetween,
  // BlockDes, // Optional: if you want a description under the title
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button, // Assuming Button component is available from your Component library
} from '../../../../components/Component'; // Adjust path if necessary
import { formatCurrency, timeAsMilliseconds } from '../../../../utils/Utils';
import { useQuery } from 'react-query';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import queryString from 'query-string';
import { Link, useNavigate } from 'react-router-dom';
import { DealCategory, newRoles } from '../../../../utils/envConfig';
import { useSelector } from 'react-redux';

// --- Placeholder for a Skeleton Loader ---
// Consider creating a ListingSkeletonLoaderShort.js for a better loading UX.
// It would be similar to DealSkeletonLoader but tailored for listing columns.
// const ListingSkeletonLoaderShort = ({ columns }) => (
//   Array.from({ length: 3 }).map((_, rowIndex) => (
//     <tr className="tb-odr-item" key={`skeleton-row-${rowIndex}`}>
//       {Array.from({ length: columns }).map((_, colIndex) => (
//         <td className="tb-odr-info bg-white text-center" key={`skeleton-cell-${rowIndex}-${colIndex}`}>
//           <span className="placeholder col-8"></span>
//         </td>
//       ))}
//     </tr>
//   ))
// );
// --- End Placeholder ---

// Custom styles for clickable rows
const clickableRowStyle = {
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
};

// Style for row hover (can be moved to CSS classes)
const hoverRowStyleJS = {
  backgroundColor: '#f8f9fa', // A light grey, adjust as needed
};

// Adapted status badge rendering for Listings
const RenderListingStatusBadge = React.memo(({ status }) => {
  const baseBadgeClass = "badge badge-sm badge-dot has-bg";
  let colorClass = "secondary"; // Default color

  switch (status?.toLowerCase()) {
    case 'active':
    case 'live':
      colorClass = 'success';
      break;
    case 'pending':
    case 'draft':
      colorClass = 'warning';
      break;
    case 'sold': // Assuming 'sold' maps to primary, adjust if needed
      colorClass = 'primary';
      break;
    case 'expired':
    case 'inactive':
    case 'rejected': // Adding rejected as danger, if applicable
      colorClass = 'danger';
      break;
    default:
      colorClass = 'secondary';
  }
  return (
    <span className={`${baseBadgeClass} bg-${colorClass} text-${colorClass}`}>
      {status || 'Unknown'}
    </span>
  );
});

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (e) {
    return 'Invalid Date';
  }
};

const RecentListings = ({ agentId, limit = 5, viewAllLink }) => {
  const axios = useAxiosPrivate();
  const navigate = useNavigate();

  const agentIdToUse = useMemo(() => agentId, [agentId]);

  const { data, isLoading, isError, error } = useQuery(
    ['agentListings', agentIdToUse, limit],
    async () => {
      try {
        const queryParams = queryString.stringify({
          // category: 'Listing Acquired', // As per your original query
          // For listings, you might have a different category or property status field to query
          // For example, if listings are just deals with a specific 'purpose' or 'listing_status':
          // purpose: 'For Sale', // or 'For Rent'
          // listing_status: 'Active', // or a list like ['Active', 'Live']
          // The endpoint /search/deal/filter suggests these are still deals.
          // For now, using the exact query from your code:
          category: 'Listing Acquired',
          agent: agentIdToUse,
          per_page: limit,
          sort_by: 'createdAt',
          sort_dir: 'DESC',
        });
        const response = await axios.get(`/search/deal/filter?${queryParams}`);
        return response.data;
      } catch (err) {
        console.error('Error fetching listings:', err);
        throw err; // Rethrow to be caught by useQuery's isError
      }
    },
    {
      staleTime: timeAsMilliseconds({ minutes: 30 }),
      // refetchInterval: timeAsMilliseconds({ minutes: 30 }), // Optional: consider if needed
      refetchOnWindowFocus: false,
      enabled: !!agentIdToUse,
      retry: 1,
    }
  );

  const listings = useMemo(() => {
    if (Array.isArray(data?.findDealsOutput)) return data.findDealsOutput;
    if (data?.deals && Array.isArray(data.deals)) return data.deals;
    if (data?.documents && Array.isArray(data.documents)) return data.documents; // From original
    if (data?.data && Array.isArray(data.data)) return data.data; // A common pattern
    return [];
  }, [data]);

  const handleRowClick = (listingId) => {
    // IMPORTANT: Verify this path. If listings are not deals,
    // this might need to navigate to /listing-details/:id or similar.
    navigate(`/deal-details/${listingId}`);
  };

  const authUser = useSelector((state) => state?.user?.loggedInUser);

  const deniedPermissions = useMemo(() => {
    if (authUser) {
      return authUser?.specialPermissions?.deny || {};
    }
  }, [authUser]);

  const checkIfLeadsBelongsToAuthUser = (deal) => {
    if (deal.category === DealCategory.SecondaryProperty) {
      return deal?.buyerAssignedAgent?.userId === authUser?.id || deal?.sellerAssignedAgent?.userId === authUser?.id;
    }
    if (deal.category === DealCategory.PrimaryOffplan) {
      return deal?.assignedAgent === authUser?.id;
    }
    return deal?.assignedAgent?.userId === authUser?.id;
  }

  return (
    <div id="recent-listings">
      <Block className="mt-4">
        <BlockHead size={'md'}>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h5">Recent Listings</BlockTitle>
              {/* <BlockDes><p>Latest listings acquired by this agent.</p></BlockDes> */}
            </BlockHeadContent>
            {viewAllLink && (
              <BlockHeadContent>
                <Button
                  color="primary"
                  outline
                  size="sm"
                  onClick={(ev) => {
                    ev.preventDefault();
                    navigate(viewAllLink); // Assuming viewAllLink is a route path
                  }}
                >
                  <Icon name={'list-ol'} /> {/* Changed icon to represent listings */}
                  <span className="d-none d-sm-inline-block ms-1">View All</span>
                </Button>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>

        <Block>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <Spinner color="primary" />
              {/* Or use a skeleton loader: <ListingSkeletonLoaderShort columns={tableColumns} /> */}
            </div>
          ) : isError ? (
            <Card className="p-4 shadow-sm">
              <div className="text-center">
                <Icon name="alert-circle" className="text-danger mb-2" style={{ fontSize: '1.5rem' }} />
                <p className="fs-6 fw-bold text-danger">Failed to load listings.</p>
                <p className="small text-muted">{error?.message || "Please try again later."}</p>
              </div>
            </Card>
          ) : !listings.length ? (
            <Card className="p-4 shadow-sm">
              <div className="text-center py-3">
                <Icon name="files" className="text-muted mb-2" style={{ fontSize: '1.5rem' }} /> {/* Icon for listings */}
                <p className="fs-6 fw-bold">No listings found.</p>
                <p className="small text-muted">This agent does not have any recent listings.</p>
              </div>
            </Card>
          ) : (
            <div className="border rounded bg-white">
              <div className="table-responsive">
                <table className="table table-orders">
                  <thead className="tb-odr-head">
                    <tr className="tb-odr-item">
                      <th className="tb-odr-info text-start ps-3"><span>Reference</span></th>
                      <th className="tb-odr-info text-start d-none d-md-table-cell"><span>Property</span></th>
                      <th className="tb-odr-info text-start d-none d-lg-table-cell"><span>Type</span></th>
                      <th className="tb-odr-info text-end"><span>Price</span></th>
                      <th className="tb-odr-info text-center d-none d-sm-table-cell"><span>Date</span></th>
                      <th className="tb-odr-info text-center pe-3"><span>Status</span></th>
                    </tr>
                  </thead>
                  <tbody className="tb-odr-body">
                    {listings.map((listing) => {
                      const listingId = listing._id || listing.id;
                      const propertyName = listing.new_listing_request?.building?.name ||
                        (listing.new_listing_request?.community?.community ?
                          `Property in ${listing.new_listing_request?.community?.community}` : 'N/A');
                      const propertyType = listing.new_listing_request?.property_type?.propertyType ||
                        (listing.new_listing_request?.beds ? `${listing.new_listing_request?.beds} Bed` : 'N/A');
                      const price = listing.new_listing_request?.price ||
                        listing.agreedSalePrice || // Legacy or alternative field
                        listing.agreed_salePrice || 0;
                      const listingPurposeType = listing.new_listing_request?.type; // e.g., Sale, Rent

                      return (
                        <tr
                          className="tb-odr-item listing-row-hover" // Added class for CSS hover
                          key={listingId}
                          style={clickableRowStyle}
                          onClick={() => handleRowClick(listingId)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverRowStyleJS.backgroundColor}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                          title="Click to view listing details"
                        >
                          <td className="tb-odr-info bg-white text-start ps-3">
                            {
                              [newRoles.Admin, newRoles.MasterAdmin].includes(authUser.role) && deniedPermissions?.dealDetails ? (
                                <span>{listing?.reference_number}</span>
                              ) : [newRoles.Admin, newRoles.MasterAdmin].includes(authUser.role) ? (
                                <Link to={`/deal-details/${listing.id ?? listing._id}`}>
                                  {listing?.reference_number}
                                </Link>
                              ) : authUser.role === newRoles.TeamLeader ?
                                (authUser?.id === listing?.assignedAgent?.userId) ? (
                                  <Link to={`/deal-details/${listing.id ?? listing._id}`}>
                                    {listing?.reference_number}
                                  </Link>
                                ) : checkIfLeadsBelongsToAuthUser(listing) ? <Link to={`/deal-details/${listing.id ?? listing._id}`}>
                                  {listing?.reference_number}
                                </Link> : (<span >{listing?.reference_number}</span>) : <Link to={`/deal-details/${listing.id ?? listing._id}`}>
                                  {listing?.reference_number}
                                </Link>
                            }
                          </td>
                          <td className="tb-odr-info bg-white text-start d-none d-md-table-cell">
                            <span className="text-wrap">{propertyName}</span>
                          </td>
                          <td className="tb-odr-info bg-white text-start d-none d-lg-table-cell">
                            {propertyType}
                          </td>
                          <td className="tb-odr-info bg-white text-end">
                            <span className="fw-bold">{formatCurrency(price)}</span>
                            {listingPurposeType && <span className="small text-muted d-block">{`(${listingPurposeType})`}</span>}
                          </td>
                          <td className="tb-odr-info bg-white text-center d-none d-sm-table-cell">
                            {formatDate(listing.createdAt || listing.creation_date)}
                          </td>
                          <td className="tb-odr-info bg-white text-center pe-3">
                            <RenderListingStatusBadge status={listing.status} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Block>
      </Block>
    </div>
  );
};

export default RecentListings;
